// @ts-strict-ignore
import React, { useMemo } from 'react';
import { OrgComponentProps, ProviderComponentProps } from '../../typescriptHelpers';
import Typography from 'antd/lib/typography';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppIdentifiersAndPlatformsFromAppIds, selectOrgProviders } from '../../selectors/selectors';
import Text from 'antd/lib/typography/Text';
import Descriptions from 'antd/lib/descriptions';
import {
    displayCapabilityForIntegrationType,
    displayTextForProviderType,
    iconForProviderType,
} from '@/helpers/integrationHelpers';
import { displayTextForAppPlatform } from '@/helpers/displayHelpers';
import { getMdmPlatformOverride } from '@/helpers/mdmPlatformOverride';
import { IntegrationProvider, IntegrationProviderType } from '@/types';
import { Divider } from 'antd/lib';
import { useMediaQuery } from 'react-responsive';
import { Tooltip } from 'antd/lib';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { DescriptionsProps } from 'antd/lib/descriptions';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Space from 'antd/lib/space';
import { EditOutlined } from '@ant-design/icons';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import { tryUpdateOrgSettings, UpdateIntegrationProviderNameRequest } from '@/actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import { flatMap } from 'lodash';

const PROVIDER_NAME_MAX_LENGTH = 30;

// Renders the Integrations details menu at the org settings level. Details are by provider, not installation.
export const IntegrationProviderSettings: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const orgProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));
    const sortedOrgProviders = useMemo(
        () =>
            [...orgProviders].sort((provider1, provider2) => {
                if (provider1.name < provider2.name) return -1;
                else if (provider1.name > provider2.name) return 1;
                else if (provider1.id < provider2.id) return -1;
                else if (provider1.id > provider2.id) return 1;

                return 0;
            }),
        [orgProviders],
    );
    const descriptionsWidth = useMediaQuery({ query: '(min-width: 1500px)' }) ? '75%' : '100%';

    const providerList = useMemo(() => {
        return sortedOrgProviders.map((provider) => {
            return (
                <React.Fragment key={provider.id}>
                    <Divider
                        orientation="left"
                        orientationMargin="0"
                        style={{ marginBottom: '8px', marginTop: '20px' }}
                    >
                        <Typography.Title
                            level={3}
                            style={{ display: 'flex', alignContent: 'center', gap: '10px', marginBottom: '0px' }}
                        >
                            <img src={iconForProviderType(provider.type)}></img>
                            {provider.type === IntegrationProviderType.CustomAnalytics && provider.name?.trim() ?
                                provider.name
                            :   displayTextForProviderType(provider.type)}
                        </Typography.Title>
                    </Divider>
                    <IntegrationProviderDetails orgId={props.orgId} provider={provider} />
                </React.Fragment>
            );
        });
    }, [sortedOrgProviders, props.orgId]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                marginTop: 12,
                marginBottom: 80,
                width: descriptionsWidth,
            }}
        >
            <Text>View details for connected integration providers across all apps in your organization.</Text>
            {providerList}
        </div>
    );
};

// Makes readable preview text and an accompanying tooltip for fields in descriptions elements.
const makeTooltipAndPreview = (
    fieldName: string,
    stringsForPreview: string[],
    itemsForTooltip: string[] | React.JSX.Element[],
): React.JSX.Element | string => {
    if (!stringsForPreview || stringsForPreview.length === 0) {
        return fieldName ? customEmptyState(fieldName) : customEmptyState();
    }

    let itemCt = 0;
    let previewString = '';

    while (itemCt < 3 && itemCt < stringsForPreview.length) {
        previewString += stringsForPreview[itemCt] + ', ';
        itemCt++;
    }

    previewString = previewString.slice(0, previewString.length - 2);
    if (stringsForPreview.length > 3) previewString += `, and ${stringsForPreview.length - 3} more`;

    return (
        <Tooltip
            title={<div style={{ padding: '20px', color: 'black' }}>{itemsForTooltip}</div>}
            color={runwayColors.white}
            overlayInnerStyle={{
                overflowY: 'scroll',
                width: 'fit-content',
                maxHeight: '200px',
                minWidth: '200px',
                maxWidth: '650px',
            }}
            destroyTooltipOnHide={true}
            placement={'top'}
        >
            <Text>{previewString}</Text>
        </Tooltip>
    );
};

// Makes readable preview text and tooltip for data in 1 dimension.
// • If there are no more than 3 elements, does not return a tooltip.
// • Optionally, you can manually disable the tooltip by passing a truthy value as an argument to the second parameter.
// • That's useful for cases where there's nested data inside a tooltip for which we dont want to spawn another tooltip.
const make1DTooltipPreview = (
    fieldName: string,
    stringsForPreview: string[],
    noTooltip = false,
): React.JSX.Element | string => {
    if (!stringsForPreview || stringsForPreview.length === 0) {
        return fieldName ? customEmptyState(fieldName) : customEmptyState();
    }

    let itemCt = 0;
    let previewString = '';

    while (itemCt < 3 && itemCt < stringsForPreview.length) {
        previewString += stringsForPreview[itemCt] + ', ';
        itemCt++;
    }

    previewString = previewString.slice(0, previewString.length - 2);
    if (stringsForPreview.length > 3) previewString += `, and ${stringsForPreview.length - 3} more`;

    if (noTooltip === true || stringsForPreview.length <= 3) return previewString;
    else {
        const itemsForTooltip = stringsForPreview.map((string) => {
            return (
                <>
                    {string}
                    <br />
                </>
            );
        });

        return (
            <Tooltip
                title={<div style={{ padding: '20px', color: 'black' }}>{itemsForTooltip}</div>}
                color={runwayColors.white}
                overlayInnerStyle={{
                    overflowY: 'scroll',
                    width: 'fit-content',
                    maxHeight: '200px',
                    minWidth: '200px',
                    maxWidth: '650',
                }}
                destroyTooltipOnHide={true}
                placement={'top'}
            >
                <Text>{previewString}</Text>
            </Tooltip>
        );
    }
};

// Handles tooltip previews for cases where we want the comment separated preview items to be "code styled."
// • Generally intended for permissions items.
const makeCodedTooltipPreview = (
    fieldName: string,
    stringsForPreview: string[],
    tooltipContent: React.JSX.Element[],
): React.JSX.Element => {
    if (!stringsForPreview || stringsForPreview.length === 0) {
        return fieldName ? customEmptyState(fieldName) : customEmptyState();
    }

    const previewElements: React.JSX.Element[] = [];
    let itemCt = 0;

    while (itemCt < 3 && itemCt < stringsForPreview.length) {
        if (itemCt === 2 || itemCt === stringsForPreview.length - 1) {
            previewElements.push(<Text code>{stringsForPreview[itemCt]}</Text>);
        } else {
            previewElements.push(
                <>
                    <Text code>{stringsForPreview[itemCt]}</Text>
                    {', '}
                </>,
            );
        }
        itemCt++;
    }

    if (stringsForPreview.length <= 3)
        return (
            <Tooltip
                title={<div style={{ padding: '20px', color: 'black' }}>{tooltipContent}</div>}
                color={runwayColors.white}
                overlayInnerStyle={{
                    overflowY: 'scroll',
                    width: 'fit-content',
                    maxHeight: '200px',
                    minWidth: '200px',
                    maxWidth: '650',
                }}
                destroyTooltipOnHide={true}
                placement={'top'}
            >
                <span>{previewElements}</span>
            </Tooltip>
        );
    else
        return (
            <Tooltip
                title={<div style={{ padding: '20px', color: 'black' }}>{tooltipContent}</div>}
                color={runwayColors.white}
                overlayInnerStyle={{
                    overflowY: 'scroll',
                    width: 'fit-content',
                    maxHeight: '200px',
                    minWidth: '200px',
                    maxWidth: '650',
                }}
                destroyTooltipOnHide={true}
                placement={'top'}
            >
                <span>
                    {previewElements}, and {stringsForPreview.length - 3} more
                </span>
            </Tooltip>
        );
};

const customEmptyState = (fieldName?: string) => {
    if (fieldName) return <Text style={{ color: antGrey.grey7 }}>No {fieldName}</Text>;
    else return <Text style={{ color: antGrey.grey7 }}>No data</Text>;
};

const providerDetailsDescProps: DescriptionsProps = {
    size: 'middle',
    layout: 'horizontal',
    bordered: true,
    column: 1,
    style: { marginBottom: 12 },
    labelStyle: { width: 300 },
};

const isProviderNameEditable = (provider: IntegrationProvider): boolean => {
    return provider.type === IntegrationProviderType.CustomAnalytics;
};

interface ProviderNameCellProps {
    orgId: string;
    provider: IntegrationProvider;
}

const ProviderNameCell: React.FC<ProviderNameCellProps> = ({ orgId, provider }) => {
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = React.useState(false);
    const [formIsValid, setFormIsValid] = React.useState(true);
    const [form] = Form.useForm<{ name: string }>();

    const updateKey = provider.id;
    const isLoading = useIsLoading(OrgSettingsUpdatable.UpdateIntegrationProviderName, updateKey);
    const updateSucceeded = useUpdated(OrgSettingsUpdatable.UpdateIntegrationProviderName, updateKey);
    const updateError = useError(OrgSettingsUpdatable.UpdateIntegrationProviderName, updateKey);

    React.useEffect(() => {
        if (updateSucceeded) {
            globalMessage.success('Provider name updated!');
            setModalOpen(false);
        }
    }, [updateSucceeded]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(`Failed to update provider name: ${updateError}`);
        }
    }, [updateError]);

    if (!isProviderNameEditable(provider)) {
        return <>{provider.name}</>;
    }

    const onFieldsChange = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));
        setFormIsValid(errors.length === 0);
    };

    const onSave = () => {
        form.validateFields()
            .then((values) => {
                const trimmed = values.name.trim();
                if (!trimmed || trimmed === provider.name) {
                    setModalOpen(false);
                    return;
                }
                const request: UpdateIntegrationProviderNameRequest = {
                    orgId,
                    providerId: provider.id,
                    name: trimmed,
                };
                dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateIntegrationProviderName, request, updateKey));
            })
            .catch(() => setFormIsValid(false));
    };

    return (
        <>
            <Space size={4} align="center">
                <Text>{provider.name}</Text>
                <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined style={{ color: runwayColors.info, fontSize: 13 }} />}
                    onClick={() => setModalOpen(true)}
                    aria-label="Edit provider name"
                />
            </Space>
            <Modal
                title="Edit custom metrics provider name"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={onSave}
                confirmLoading={isLoading}
                okText="Save"
                okButtonProps={{ disabled: !formIsValid, loading: isLoading }}
                afterClose={() => {
                    form.resetFields();
                    setFormIsValid(true);
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ name: provider.name }}
                    onFieldsChange={onFieldsChange}
                    preserve={false}
                >
                    <Form.Item
                        name="name"
                        label="Provider name"
                        style={{ marginBottom: 8 }}
                        rules={[
                            { required: true, message: 'Provider name is required' },
                            {
                                max: PROVIDER_NAME_MAX_LENGTH,
                                message: `Provider name must be ${PROVIDER_NAME_MAX_LENGTH} characters or fewer`,
                            },
                        ]}
                    >
                        <Input onPressEnter={onSave} />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        The display name for this provider shown across Runway, including custom metric configuration.
                    </Text>
                </Form>
            </Modal>
        </>
    );
};

const IntegrationProviderDetails: React.FC<ProviderComponentProps> = ({ orgId, provider }) => {
    const connectedApps = useSelector((state: RootState) =>
        selectAppIdentifiersAndPlatformsFromAppIds(state, provider.appIds),
    );
    const connectedAppsPreviewStrings: string[] = [];
    connectedApps.forEach((app) => {
        const installedIntegrationTypes = provider.installedIntegrationTypesByAppId[app.appId] ?? [];
        const appIntegrationsForProvider = installedIntegrationTypes.map((integrationType) =>
            displayCapabilityForIntegrationType(integrationType),
        );
        const appIntegrationsTerm =
            appIntegrationsForProvider.length > 0 ? ` (${appIntegrationsForProvider.join(', ')})` : '';

        connectedAppsPreviewStrings.push(
            `${app.appName} ${getMdmPlatformOverride(app.shouldDisplayAsMdm)?.label ?? displayTextForAppPlatform(app.platform)}${appIntegrationsTerm}`,
        );
    });

    const connectedAppsLabel = (
        <Text style={{ display: 'flex', alignContent: 'center', gap: '10px' }}>
            Connected apps
            <TooltipInfoIcon tooltipContent="The apps in Runway that have active, connected integrations using this provider." />
        </Text>
    );
    const makeConnectedAppsContent = () => {
        if (connectedAppsPreviewStrings.length === 0)
            return <Text style={{ color: antGrey.grey7 }}>No connected apps</Text>;
        else return make1DTooltipPreview(null, connectedAppsPreviewStrings);
    };

    const installationIdsPreviewStrings: string[] = [];
    const installationIdsByApp = connectedApps.map((app) => {
        const appInstallations = [...(provider.installationsByAppId?.[app.appId] ?? [])].sort((a, b) =>
            displayCapabilityForIntegrationType(a.integrationType).localeCompare(
                displayCapabilityForIntegrationType(b.integrationType),
            ),
        );
        const appInstallationItems = appInstallations.map((installation) => {
            installationIdsPreviewStrings.push(installation.installationId);
            return (
                <>
                    {displayCapabilityForIntegrationType(installation.integrationType)}:{' '}
                    {installation.installationId || customEmptyState()}
                    <br />
                </>
            );
        });

        return (
            <>
                <Text
                    strong
                >{`${app.appName} ${getMdmPlatformOverride(app.shouldDisplayAsMdm)?.label ?? displayTextForAppPlatform(app.platform)}:`}</Text>
                <br />
                {appInstallationItems}
                <br />
            </>
        );
    });

    const installationIdsItem = (
        <Descriptions.Item label={<Text>Installation IDs</Text>}>
            {makeTooltipAndPreview('installation ids', installationIdsPreviewStrings, installationIdsByApp)}
        </Descriptions.Item>
    );

    switch (provider.type) {
        case IntegrationProviderType.Amazon: {
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Client ID</Text>}>
                        {provider.data.clientId || customEmptyState('client id')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Amplitude: {
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Should use EU server</Text>}>
                        {provider.data.shouldUseEUServer === true ?
                            'True'
                        : provider.data.shouldUseEUServer === false ?
                            'False'
                        :   customEmptyState()}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Apple: {
            const appsPreviewStrings =
                provider.data.availableApps ? provider.data.availableApps.map((app) => String(app.bundleId)) : [];
            const apps =
                provider.data.availableApps ?
                    provider.data.availableApps.map((app) => {
                        const betaGroupsPreviewStrings =
                            app.betaGroups ? app.betaGroups.map((group) => group.name) : [];

                        return (
                            <>
                                App ID: {app.appId || customEmptyState()}
                                <br />
                                Bundle ID: {app.bundleId || customEmptyState()}
                                <br />
                                Beta Groups: {make1DTooltipPreview(null, betaGroupsPreviewStrings, true)}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const productsPreviewStrings: string[] = [];
            const products =
                provider.data.availableProductsAndWorkflows ?
                    provider.data.availableProductsAndWorkflows.map((product) => {
                        productsPreviewStrings.push(product.product.name);
                        const workflowsPreviewStrings =
                            product.workflows ? product.workflows.map((workflow) => workflow.name) : [];

                        return (
                            <>
                                Product Name: {product.product.name}
                                <br />
                                Product ID: {product.product.id}
                                <br />
                                Product Workflows: {make1DTooltipPreview(null, workflowsPreviewStrings, false)}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const workflowsPreviewStrings: string[] = [];
            const workflows =
                provider.data.availableProductsAndWorkflows ?
                    provider.data.availableProductsAndWorkflows.map((product) => {
                        const projectWorkflows =
                            product.workflows ?
                                product.workflows.map((workflow) => {
                                    workflowsPreviewStrings.push(workflow.name);
                                    return (
                                        <>
                                            Workflow Name: {workflow.name || customEmptyState()}
                                            <br />
                                            Workflow ID: {workflow.id || customEmptyState()}
                                            <br />
                                            Is Enabled:{' '}
                                            {workflow.isEnabled === true ?
                                                'True'
                                            : workflow.isEnabled === false ?
                                                'False'
                                            :   customEmptyState()}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                <Text strong>{product.product.name + ':'}</Text>
                                <br />
                                <br />
                                {projectWorkflows}
                            </>
                        );
                    })
                :   [];

            return (
                <>
                    <Descriptions {...providerDetailsDescProps}>
                        <Descriptions.Item label={<Text>Provider name</Text>}>
                            <ProviderNameCell orgId={orgId} provider={provider} />
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                        <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                        {installationIdsItem}
                        <Descriptions.Item label={<Text>Available apps</Text>}>
                            {makeTooltipAndPreview('available apps', appsPreviewStrings, apps)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Available products</Text>}>
                            {makeTooltipAndPreview('available products', productsPreviewStrings, products)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Available workflows</Text>}>
                            {makeTooltipAndPreview('available workflows', workflowsPreviewStrings, workflows)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Key ID</Text>}>
                            {provider.data.keyId || customEmptyState('key id')}
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Issuer ID</Text>}>
                            {provider.data.issuerId || customEmptyState('issuer id')}
                        </Descriptions.Item>
                    </Descriptions>
                </>
            );
        }
        case IntegrationProviderType.Asana: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project GID: {project.gid || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const sectionsPreviewStrings = provider.data.availableSections || [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available sections</Text>}>
                        {make1DTooltipPreview('available sections', sectionsPreviewStrings)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Azure: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjectsAndResources ?
                    provider.data.availableProjectsAndResources.map((project) => {
                        projectsPreviewStrings.push(project.project.name);
                        return (
                            <>
                                Project Name: {project.project.name || customEmptyState()}
                                <br />
                                Project ID: {project.project.id || customEmptyState()}
                                <br />
                                Project URL: {project.project.url || customEmptyState()}
                                <br />
                                Org ID: {project.project.orgId || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const reposPreviewStrings: string[] = [];
            const repos =
                provider.data.availableProjectsAndResources ?
                    provider.data.availableProjectsAndResources.map((project) => {
                        const projectRepos =
                            project.repos ?
                                project.repos.map((repo) => {
                                    reposPreviewStrings.push(repo.name);
                                    return (
                                        <>
                                            Repo Name: {repo.name || customEmptyState()}
                                            <br />
                                            Repo ID: {repo.id || customEmptyState()}
                                            <br />
                                            Project ID: {repo.id || customEmptyState()}
                                            <br />
                                            Default Branch: {repo.defaultBranch || customEmptyState()}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                <Text strong>{project.project.name + ':'}</Text>
                                <br />
                                <br />
                                {projectRepos}
                            </>
                        );
                    })
                :   [];

            const pipelinesPreviewStrings: string[] = [];
            const pipelines =
                provider.data.availableProjectsAndResources ?
                    provider.data.availableProjectsAndResources.map((project) => {
                        const projectPipelines =
                            project.pipelines ?
                                project.pipelines.map((pipeline) => {
                                    pipelinesPreviewStrings.push(pipeline.name);
                                    return (
                                        <>
                                            Pipeline Name: {pipeline.name || customEmptyState()}
                                            <br />
                                            Pipeline ID: {pipeline.id || customEmptyState()}
                                            <br />
                                            Pipeline URL: {pipeline.url || customEmptyState()}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                <Text strong>{project.project.name + ':'}</Text>
                                <br />
                                <br />
                                {projectPipelines}
                            </>
                        );
                    })
                :   [];

            const workAreasPreviewStrings: string[] = [];
            const workAreas =
                provider.data.availableProjectsAndResources ?
                    provider.data.availableProjectsAndResources.map((project) => {
                        const projectWorkAreas =
                            project.workAreas ?
                                project.workAreas.map((workArea) => {
                                    workAreasPreviewStrings.push(workArea.path);
                                    return (
                                        <>
                                            Work Area Path: {workArea.path || customEmptyState()}
                                            <br />
                                            Work Area ID: {workArea.id || customEmptyState()}
                                            <br />
                                            Work Area Name: {workArea.name || customEmptyState()}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                <Text strong>{project.project.name + ':'}</Text>
                                <br />
                                <br />
                                {projectWorkAreas}
                            </>
                        );
                    })
                :   [];

            const boardColumnsPreviewStrings: string[] = [];
            const boardColumns =
                provider.data.availableProjectsAndResources ?
                    provider.data.availableProjectsAndResources.map((project) => {
                        const projectBoardColumns =
                            project.boardColumns ?
                                project.boardColumns.map((column) => {
                                    boardColumnsPreviewStrings.push(column.name);
                                    return (
                                        <>
                                            {column.name}
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                <Text strong>{project.project.name + ':'}</Text>
                                <br />
                                <br />
                                {projectBoardColumns}
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available repos</Text>}>
                        {makeTooltipAndPreview('available repos', reposPreviewStrings, repos)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available pipelines</Text>}>
                        {makeTooltipAndPreview('available pipelines', pipelinesPreviewStrings, pipelines)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available work areas</Text>}>
                        {makeTooltipAndPreview('available work areas', workAreasPreviewStrings, workAreas)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available board columns</Text>}>
                        {makeTooltipAndPreview('available board columns', boardColumnsPreviewStrings, boardColumns)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Bitbucket: {
            const workspacesStrings: string[] = [];
            const workspaces =
                provider.data.availableWorkspacesAndRepos ?
                    provider.data.availableWorkspacesAndRepos.map((workspace) => {
                        workspacesStrings.push(workspace.workspaceSlug);
                        return (
                            <>
                                Workspace Slug: {workspace.workspaceSlug}
                                <br />
                                Workspace ID: {workspace.workspaceId}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const reposStrings: string[] = [];
            const repos =
                provider.data.availableWorkspacesAndRepos ?
                    provider.data.availableWorkspacesAndRepos.map((workspace) => {
                        const orgRepos =
                            workspace.repos ?
                                workspace.repos.map((repo) => {
                                    reposStrings.push(repo.name);
                                    return (
                                        <>
                                            Repo Name: {repo.name}
                                            <br />
                                            Repo ID: {repo.id}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                {workspace.workspaceId + ':'}
                                <br />
                                <br />
                                {orgRepos}
                            </>
                        );
                    })
                :   [];

            const permissionsStrings: string[] = [];
            const permissions =
                provider.data.bitbucketPermissions ?
                    provider.data.bitbucketPermissions.map((permission) => {
                        permissionsStrings.push(permission.type);
                        return (
                            <>
                                <Text code>
                                    {permission.type}: {permission.value}
                                </Text>
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available workspaces</Text>}>
                        {makeTooltipAndPreview('available workspaces', workspacesStrings, workspaces)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available repos</Text>}>
                        {makeTooltipAndPreview('available repos', reposStrings, repos)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Permissions</Text>}>
                        {makeCodedTooltipPreview('available permissions', permissionsStrings, permissions)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Bitrise: {
            const appsPreviewStrings: string[] = [];
            const apps =
                provider.data.availableApps ?
                    provider.data.availableApps.map((app) => {
                        appsPreviewStrings.push(app.title);
                        const workflowPreviewStrings = !app.workflows ? [] : app.workflows;

                        return (
                            <>
                                App Name: {app.title || customEmptyState()}
                                <br />
                                App Slug: {app.slug || customEmptyState()}
                                <br />
                                App Workflows: {make1DTooltipPreview(null, workflowPreviewStrings, true)}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available apps</Text>}>
                        {makeTooltipAndPreview('available apps', appsPreviewStrings, apps)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Bugsnag: {
            const orgsStrings: string[] = [];
            const orgs =
                provider.data.availableOrgsAndProjects ?
                    provider.data.availableOrgsAndProjects.map((org) => {
                        orgsStrings.push(org.orgName);
                        return (
                            <>
                                Org Name: {org.orgName || customEmptyState()}
                                <br />
                                Org ID: {org.orgId || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const projectStrings: string[] = [];
            const projects =
                provider.data.availableOrgsAndProjects ?
                    provider.data.availableOrgsAndProjects.map((org) => {
                        const orgProjects =
                            org.projects ?
                                org.projects.map((project) => {
                                    projectStrings.push(project.name);
                                    return (
                                        <>
                                            Project Name: {project.name || customEmptyState()}
                                            <br />
                                            Project ID: {project.id || customEmptyState()}
                                            <br />
                                            Release Stages: {make1DTooltipPreview(null, project.releaseStages, true)}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                {<Text strong>{org.orgId + ':'}</Text>}
                                <br />
                                <br />
                                {orgProjects}
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available orgs</Text>}>
                        {makeTooltipAndPreview('available orgs', orgsStrings, orgs)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectStrings, projects)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Buildkite: {
            const orgsStrings: string[] = [];
            const orgs =
                provider.data.availableOrgsAndPipelines ?
                    provider.data.availableOrgsAndPipelines.map((org) => {
                        orgsStrings.push(org.org.name);

                        return (
                            <>
                                Org Name: {org.org.name || customEmptyState()}
                                <br />
                                Org ID: {org.org.id || customEmptyState()}
                                <br />
                                Org Slug: {org.org.slug || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const pipelinesStrings: string[] = [];
            const pipelines =
                provider.data.availableOrgsAndPipelines ?
                    provider.data.availableOrgsAndPipelines.map((org) => {
                        const orgPipelines =
                            org.pipelines ?
                                org.pipelines.map((pipeline) => {
                                    pipelinesStrings.push(pipeline.name);
                                    return (
                                        <>
                                            Pipeline Name: {pipeline.name || customEmptyState()}
                                            <br />
                                            Pipeline ID: {pipeline.id || customEmptyState()}
                                            <br />
                                            Pipeline Slug: {pipeline.slug || customEmptyState()}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                <Text strong>{org.org.name + ':'}</Text>
                                <br />
                                <br />
                                {orgPipelines}
                            </>
                        );
                    })
                :   [];

            const tokenScopesStrings = provider.data.tokenScopes || [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available orgs</Text>}>
                        {makeTooltipAndPreview('available orgs', orgsStrings, orgs)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available pipelines</Text>}>
                        {makeTooltipAndPreview('available pipelines', pipelinesStrings, pipelines)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Token scopes</Text>}>
                        {make1DTooltipPreview('token scopes', tokenScopesStrings)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Codemagic: {
            const appsPreviewStrings: string[] = [];
            const apps =
                provider.data.availableApps ?
                    provider.data.availableApps.map((app) => {
                        appsPreviewStrings.push(app.appName);

                        const workflowPreviewStrings: string[] = [];
                        if (app.workflows) {
                            for (const key in app.workflows) {
                                workflowPreviewStrings.push(app.workflows[key].name);
                            }
                        }

                        return (
                            <>
                                App Name: {app.appName || customEmptyState()}
                                <br />
                                App ID: {app.id || customEmptyState()}
                                <br />
                                App Workflows: {make1DTooltipPreview(null, workflowPreviewStrings, true)}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available apps</Text>}>
                        {makeTooltipAndPreview('available apps', appsPreviewStrings, apps)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Crowdin: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.projects ?
                    provider.data.projects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project ID: {project.id || customEmptyState()}
                                <br />
                                Project Identifier: {project.identifier || customEmptyState()}
                                <br />
                                Project Key: {project.key || customEmptyState()}
                                <br />
                                Project URL: {project.url || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    {provider.data.onPremDomain !== undefined && provider.data.onPremDomain.length > 0 ?
                        <Descriptions.Item label={<Text>On prem domain</Text>}>
                            {provider.data.onPremDomain}
                        </Descriptions.Item>
                    :   null}
                </Descriptions>
            );
        }
        case IntegrationProviderType.Datadog: {
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Application key</Text>}>
                        {provider.data.applicationKey || customEmptyState('application key')}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Data center</Text>}>
                        {provider.data.dataCenter || customEmptyState('data center')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Embrace: {
            const appsPreviewStrings = provider.data.allApps ? provider.data.allApps.map((app) => app.name) : [];
            const apps =
                provider.data.allApps ?
                    provider.data.allApps.map((app) => {
                        return (
                            <>
                                App Name: {app.name}
                                <br />
                                App ID: {app.id}
                                <br />
                                Platform: {app.platform}
                                <br />
                                Environment: {app.environment}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available apps</Text>}>
                        {makeTooltipAndPreview('available apps', appsPreviewStrings, apps)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.GitHub: {
            const reposPreviewStrings = provider.data.availableRepos || [];

            const workflowsPreviewStrings: string[] = [];
            const workflows: React.JSX.Element[] = [];
            if (provider.data.availableWorkflows) {
                for (const appID in provider.data.availableWorkflows) {
                    const repoWorkflows = provider.data.availableWorkflows[appID].map((workflow) => {
                        workflowsPreviewStrings.push(workflow.name);

                        return (
                            <>
                                Workflow Name: {workflow.name || customEmptyState()}
                                <br />
                                Workflow ID: {workflow.id || customEmptyState()}
                                <br />
                                Workflow URL: {workflow.url || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    });

                    workflows.push(
                        <>
                            {<Text strong>{appID + ':'}</Text>}
                            <br />
                            <br />
                            {repoWorkflows}
                        </>,
                    );
                }
            }

            const permissionsPreviewStrings: string[] = [];
            const permissions =
                provider.data.gitHubPermissions ?
                    provider.data.gitHubPermissions.map((permission) => {
                        permissionsPreviewStrings.push(String(permission.type));

                        return (
                            <>
                                <Text code>
                                    {permission.type}: {permission.value}
                                </Text>
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <>
                    <Descriptions {...providerDetailsDescProps}>
                        <Descriptions.Item label={<Text>Provider name</Text>}>
                            <ProviderNameCell orgId={orgId} provider={provider} />
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                        <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                        {installationIdsItem}
                        <Descriptions.Item label={<Text>Available repositories</Text>}>
                            {make1DTooltipPreview('available repositories', reposPreviewStrings)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Available workflows</Text>}>
                            {makeTooltipAndPreview('available workflows', workflowsPreviewStrings, workflows)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text>Permissions</Text>}>
                            {makeCodedTooltipPreview('permissions', permissionsPreviewStrings, permissions)}
                        </Descriptions.Item>
                        {provider.data.onPremUrl !== undefined && provider.data.onPremUrl.length > 0 ?
                            <Descriptions.Item label={<Text>On prem URL</Text>}>
                                {provider.data.onPremUrl}
                            </Descriptions.Item>
                        :   null}
                        <Descriptions.Item label={<Text>GitHub app ID</Text>}>
                            {provider.data.gitHubAppId || customEmptyState('github app id')}
                        </Descriptions.Item>
                    </Descriptions>
                </>
            );
        }
        case IntegrationProviderType.GitLab: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.displayableName);
                        return (
                            <>
                                Project Name: {project.displayableName || customEmptyState()}
                                <br />
                                Project ID: {project.projectId || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const deployJobPreviewStrings = provider.data.possibleCiDeployJobNames || [];

            const permissionsPreviewStrings =
                provider.data.gitLabPermissions ?
                    provider.data.gitLabPermissions.map((permission) => String(permission.type))
                :   [];
            const permissions =
                provider.data.gitLabPermissions ?
                    provider.data.gitLabPermissions.map((permission) => {
                        return (
                            <>
                                <Text code>{`${permission.type}: ${permission.value}`}</Text>
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available CI deploy jobs</Text>}>
                        {make1DTooltipPreview('available ci deploy jobs', deployJobPreviewStrings)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Permissions</Text>}>
                        {makeCodedTooltipPreview('permissions', permissionsPreviewStrings, permissions)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Google: {
            const firebaseData = provider.data.projectsResources;
            const analyticsResources = provider.data.analyticsResources;

            const firebasePreviewStrings: string[] = [];
            const firebaseProjects =
                firebaseData ?
                    firebaseData.map((project) => {
                        firebasePreviewStrings.push(project.project.displayName);
                        return (
                            <>
                                Project Name: {project.project.displayName || customEmptyState()}
                                <br />
                                Project ID: {project.project.projectId || customEmptyState()}
                                <br />
                                Project Number: {project.project.projectNumber || customEmptyState()}
                                <br />
                                Project State: {project.project.state || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const appsPreviewStrings: string[] = [];
            const appsByProjectID =
                firebaseData ?
                    firebaseData.map((project) => {
                        const projectApps: React.JSX.Element[] = [];

                        if (project.availableAndroidApps) {
                            project.availableAndroidApps.forEach((app) => {
                                appsPreviewStrings.push(app.displayName);
                                projectApps.push(
                                    <>
                                        {`App Name`}: {app.displayName || customEmptyState()}
                                        <br />
                                        {`App Platform`}: Android
                                        <br />
                                        {`App ID`}: {app.appId || customEmptyState()}
                                        <br />
                                        {`Package Name:`} {app.packageName || customEmptyState()}
                                        <br />
                                        <br />
                                    </>,
                                );
                            });
                        }

                        if (project.availableIosApps) {
                            project.availableIosApps.forEach((app) => {
                                appsPreviewStrings.push(app.displayName);
                                projectApps.push(
                                    <>
                                        {`App Name: `} {app.displayName || customEmptyState()}
                                        <br />
                                        {`App Platform: `}: iOS
                                        <br />
                                        {`App ID: `} {app.appId || customEmptyState()}
                                        <br />
                                        {`Bundle ID: `} {app.bundleId || customEmptyState()}
                                        <br />
                                        <br />
                                    </>,
                                );
                            });
                        }

                        return (
                            <>
                                <Text strong>{`${project.project.projectId}:`}</Text>
                                <br />
                                <br />
                                {projectApps}
                            </>
                        );
                    })
                :   [];

            const betaGroupsPreviewStrings: string[] = [];
            const betaGroupsByProjectId =
                firebaseData ?
                    firebaseData.map((project) => {
                        const projectBetaGroups: React.JSX.Element[] = [];

                        if (project.availableBetaGroups) {
                            project.availableBetaGroups.forEach((betaGroup) => {
                                betaGroupsPreviewStrings.push(betaGroup.name);
                                const testersPreviewStrings =
                                    betaGroup.groupTesters ?
                                        betaGroup.groupTesters.map((tester) => tester.displayName)
                                    :   [];

                                projectBetaGroups.push(
                                    <>
                                        Group Name: {betaGroup.name || customEmptyState()}
                                        <br />
                                        Group Alias: {betaGroup.alias || customEmptyState()}
                                        <br />
                                        Group ID: {betaGroup.id || customEmptyState()}
                                        <br />
                                        Group Testers: {make1DTooltipPreview(null, testersPreviewStrings)}
                                        <br />
                                        <br />
                                    </>,
                                );
                            });
                        }

                        return (
                            <>
                                <Text strong>{`${project.project.projectId}: `}</Text>
                                <br />
                                <br />
                                {projectBetaGroups}
                            </>
                        );
                    })
                :   [];

            const analyticsPreviewStrings: string[] = [];
            const analyticsMetrics =
                analyticsResources && analyticsResources.allMetrics ?
                    analyticsResources.allMetrics.map((metric) => {
                        analyticsPreviewStrings.push(metric.name);
                        return (
                            <>
                                Name: {metric.name || customEmptyState()}
                                <br />
                                API Name: {metric.apiName || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const dataStreamPreviewStrings: string[] = [];
            const dataStreams =
                analyticsResources && analyticsResources.dataStreams ?
                    analyticsResources.dataStreams.map((dataStream) => {
                        dataStreamPreviewStrings.push(dataStream.name);
                        return (
                            <>
                                Name: {dataStream.name || customEmptyState()}
                                <br />
                                ID: {dataStream.id || customEmptyState()}
                                <br />
                                Platform: {dataStream.platform || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const eventNamePreviewStrings =
                analyticsResources && analyticsResources.allEventNames ? analyticsResources.allEventNames : [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider Name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Firebase projects</Text>}>
                        {makeTooltipAndPreview('firebase projects', firebasePreviewStrings, firebaseProjects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Firebase apps</Text>}>
                        {makeTooltipAndPreview('firebase apps', appsPreviewStrings, appsByProjectID)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Firebase beta groups</Text>}>
                        {makeTooltipAndPreview('firebase beta groups', betaGroupsPreviewStrings, betaGroupsByProjectId)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available analytics metrics</Text>}>
                        {makeTooltipAndPreview(
                            'available analytics metrics',
                            analyticsPreviewStrings,
                            analyticsMetrics,
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available data streams</Text>}>
                        {makeTooltipAndPreview('available data streams', dataStreamPreviewStrings, dataStreams)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available event names</Text>}>
                        {make1DTooltipPreview('available event names', eventNamePreviewStrings)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Developer ID</Text>}>
                        {provider.data.developerId || customEmptyState('developer id')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Mixpanel: {
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Should use EU server</Text>}>
                        {provider.data.shouldUseEUServer === true ?
                            'True'
                        : provider.data.shouldUseEUServer === false ?
                            'False'
                        :   customEmptyState()}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Monday: {
            const boardsStrings: string[] = [];
            const boards =
                provider.data.availableBoards ?
                    provider.data.availableBoards.map((board) => {
                        boardsStrings.push(board.name);
                        const columnsStrings = board.columns ? board.columns.map((column) => column.name) : [];

                        return (
                            <>
                                Board Name: {board.name || customEmptyState()}
                                <br />
                                Board ID: {board.id || customEmptyState()}
                                <br />
                                Board Columns: {make1DTooltipPreview(null, columnsStrings, true)}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available boards</Text>}>
                        {makeTooltipAndPreview('available boards', boardsStrings, boards)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Jenkins: {
            const previewStrings = provider.data.availableJobsOrWorkflows || [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Username</Text>}>
                        {provider.data.username || customEmptyState('username')}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Host URL</Text>}>
                        {provider.data.url || customEmptyState('host URL')}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available jobs/workflows</Text>}>
                        {make1DTooltipPreview('available jobs/workflows', previewStrings)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Jira: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project ID: {project.id || customEmptyState()}
                                <br />
                                Project Key: {project.key || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const doneStatusesPreviewStrings = provider.data.doneStatuses || [];
            const additionalStatusesPreviewStrings = provider.data.availableAdditionalDoneStatuses || [];

            const permissionsPreviewStrings: string[] = [];
            const permissions =
                provider.data.permissions ?
                    provider.data.permissions.map((permission) => {
                        permissionsPreviewStrings.push(permission.type);
                        return (
                            <>
                                {
                                    <Text code>
                                        {permission.type}: {permission.value}
                                    </Text>
                                }
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Permissions</Text>}>
                        {makeCodedTooltipPreview('available permissions', permissionsPreviewStrings, permissions)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Default done statuses</Text>}>
                        {make1DTooltipPreview('default done statuses', doneStatusesPreviewStrings)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>All statuses</Text>}>
                        {make1DTooltipPreview('statuses', additionalStatusesPreviewStrings)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Cloud URL</Text>}>
                        {provider.data.cloudUrl || customEmptyState('cloud URL')}
                    </Descriptions.Item>
                    {provider.data.onPremUrl !== undefined && provider.data.onPremUrl.length > 0 ?
                        <Descriptions.Item label={<Text>On prem URL</Text>}>
                            {provider.data.onPremUrl}
                        </Descriptions.Item>
                    :   null}
                </Descriptions>
            );
        }

        case IntegrationProviderType.JiraServiceManagement: {
            const schedulesPreviewStrings: string[] = [];
            const schedules =
                provider.data.availableSchedules ?
                    provider.data.availableSchedules.map((schedule) => {
                        schedulesPreviewStrings.push(schedule.name);
                        return (
                            <>
                                Schedule Name: {schedule.name || customEmptyState()}
                                <br />
                                Schedule ID: {schedule.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const rotationsPreviewStrings: string[] = [];
            const rotations =
                provider.data.availableRotations ?
                    provider.data.availableRotations.map((rotation) => {
                        rotationsPreviewStrings.push(rotation.name);
                        return (
                            <>
                                Rotation Name: {rotation.name || customEmptyState()}
                                <br />
                                Rotation ID: {rotation.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available schedules</Text>}>
                        {makeTooltipAndPreview('available schedules', schedulesPreviewStrings, schedules)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available rotations</Text>}>
                        {makeTooltipAndPreview('available rotations', rotationsPreviewStrings, rotations)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Cloud URL</Text>}>
                        {provider.data.cloudUrl || customEmptyState('cloud URL')}
                    </Descriptions.Item>
                    {provider.data.onPremUrl !== undefined && provider.data.onPremUrl.length > 0 ?
                        <Descriptions.Item label={<Text>On prem URL</Text>}>
                            {provider.data.onPremUrl}
                        </Descriptions.Item>
                    :   null}
                </Descriptions>
            );
        }

        case IntegrationProviderType.PagerDuty:
        case IntegrationProviderType.Rootly:
        case IntegrationProviderType.IncidentIo: {
            const previewStrings: string[] = [];
            const schedules =
                provider.data.schedules ?
                    provider.data.schedules.map((schedule) => {
                        previewStrings.push(schedule.name);
                        return (
                            <>
                                Schedule Name: {schedule.name}
                                <br />
                                Schedule ID: {schedule.id}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Schedules</Text>}>
                        {makeTooltipAndPreview('schedules', previewStrings, schedules)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.LaunchDarkly: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.projects ?
                    provider.data.projects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project ID: {project.id || customEmptyState()}
                                <br />
                                Project Key: {project.key || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const environmentsPreviewStrings: string[] = [];
            const environments: React.JSX.Element[] = [];
            if (provider.data.environments) {
                for (const projectID in provider.data.environments) {
                    const projEnvironments: React.JSX.Element[] = [];

                    for (const singleEnvironment of provider.data.environments[projectID]) {
                        environmentsPreviewStrings.push(singleEnvironment.name);
                        projEnvironments.push(
                            <>
                                Environment Name: {singleEnvironment.name || customEmptyState()}
                                <br />
                                Environment ID: {singleEnvironment.id || customEmptyState()}
                                <br />
                                Environment Key: {singleEnvironment.key || customEmptyState()}
                                <br />
                                <br />
                            </>,
                        );
                    }

                    environments.push(
                        <>
                            <Text strong>{projectID + ':'}</Text>
                            <br />
                            <br />
                            {projEnvironments}
                        </>,
                    );
                }
            }

            const tagsPreviewStrings = provider.data.tags || [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available environments</Text>}>
                        {makeTooltipAndPreview('available environments', environmentsPreviewStrings, environments)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available tags</Text>}>
                        {make1DTooltipPreview('available tags', tagsPreviewStrings)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Pivotal: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.name);

                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project ID: {project.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Linear: {
            const doneStatusesPreviewStrings = provider.data.doneStatuses || [];
            const additionalStatusesPreviewStrings = provider.data.availableAdditionalDoneStatuses || [];

            const teamsPreviewStrings: string[] = [];
            const teams =
                provider.data.availableTeams ?
                    provider.data.availableTeams.map((team) => {
                        teamsPreviewStrings.push(team.name);
                        return (
                            <>
                                Team Name: {team.name || customEmptyState()}
                                <br />
                                Team ID: {team.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available teams</Text>} span={1}>
                        {makeTooltipAndPreview('available teams', teamsPreviewStrings, teams)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Default done statuses</Text>} span={1}>
                        {make1DTooltipPreview('default done statuses', doneStatusesPreviewStrings)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>All statuses</Text>} span={1}>
                        {make1DTooltipPreview('statuses', additionalStatusesPreviewStrings)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Optimizely: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.projects ?
                    provider.data.projects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project ID: {project.id || customEmptyState()}
                                <br />
                                Uses Flags API:{' '}
                                {project.isUsingFlagsAPI === true ?
                                    'True'
                                : project.isUsingFlagsAPI === false ?
                                    'False'
                                :   customEmptyState()}
                                <br />
                                Is Classic:{' '}
                                {project.isClassic === true ?
                                    'True'
                                : project.isClassic === false ?
                                    'False'
                                :   customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const environmentsPreviewStrings: string[] = [];
            const environments: React.JSX.Element[] = [];
            if (provider.data.environments) {
                for (const projectID in provider.data.environments) {
                    const environmentsForProject = provider.data.environments[projectID].map((environment) => {
                        environmentsPreviewStrings.push(environment.name);
                        return (
                            <>
                                Environment Name: {environment.name || customEmptyState()}
                                <br />
                                Environment ID: {environment.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    });

                    environments.push(
                        <>
                            <Text strong>{projectID + ':'}</Text>
                            <br />
                            <br />
                            {environmentsForProject}
                        </>,
                    );
                }
            }

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available environments</Text>}>
                        {makeTooltipAndPreview('available environments', environmentsPreviewStrings, environments)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.RunwayBuildDistro: {
            const bucketsStrings: string[] = [];
            const bucketsByAppID: React.JSX.Element[] = [];

            if (provider.data.availableAppsAndBuckets) {
                for (const appID in provider.data.availableAppsAndBuckets) {
                    const appBuckets = provider.data.availableAppsAndBuckets[appID].map((bucket) => {
                        bucketsStrings.push(bucket.name);
                        return (
                            <>
                                Bucket Name: {bucket.name || customEmptyState()}
                                <br />
                                Bucket ID: {bucket.id || customEmptyState()}
                                <br />
                                Bucket Type: {bucket.type || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    });

                    bucketsByAppID.push(
                        <>
                            <Text strong>{appID + ':'}</Text>
                            <br />
                            <br />
                            {appBuckets}
                        </>,
                    );
                }
            }

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available buckets</Text>} span={1}>
                        {makeTooltipAndPreview('available buckets', bucketsStrings, bucketsByAppID)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Sentry: {
            const orgsPreviewStrings: string[] = [];
            const orgs =
                provider.data.availableOrgsAndProjects ?
                    provider.data.availableOrgsAndProjects.map((org) => {
                        orgsPreviewStrings.push(org.orgName);
                        return (
                            <>
                                Org Name: {org.orgName || customEmptyState()}
                                <br />
                                Org ID: {org.orgId || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableOrgsAndProjects ?
                    provider.data.availableOrgsAndProjects.map((org) => {
                        const orgProjects =
                            org.projects ?
                                org.projects.map((project) => {
                                    projectsPreviewStrings.push(project.displayableName);
                                    const environmentsPreviewStrings = project.environments || [];

                                    return (
                                        <>
                                            Project Name: {project.displayableName || customEmptyState()}
                                            <br />
                                            Project ID: {project.id || customEmptyState()}
                                            <br />
                                            Project Environments:{' '}
                                            {make1DTooltipPreview(null, environmentsPreviewStrings, true)}
                                            <br />
                                            <br />
                                        </>
                                    );
                                })
                            :   [];

                        return (
                            <>
                                <Text strong>{org.orgId + ':'}</Text>
                                <br />
                                <br />
                                {orgProjects}
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available orgs</Text>} span={1}>
                        {makeTooltipAndPreview('available orgs', orgsPreviewStrings, orgs)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available projects</Text>} span={1}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Shortcut: {
            const doneStatusesStrings =
                provider.data.doneStatuses ? provider.data.doneStatuses.map((status) => status.name) : [];
            const doneStatuses =
                provider.data.doneStatuses ?
                    provider.data.doneStatuses.map((status) => {
                        return (
                            <>
                                Name: {status.name || customEmptyState()}
                                <br />
                                Identifier: {status.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const additionalStatusesStrings =
                provider.data.availableAdditionalDoneStatuses ?
                    provider.data.availableAdditionalDoneStatuses.map((status) => status.name)
                :   [];
            const additionalAvailableDoneStatuses =
                provider.data.availableAdditionalDoneStatuses ?
                    provider.data.availableAdditionalDoneStatuses.map((additionalStatus) => {
                        return (
                            <>
                                Name: {additionalStatus.name || customEmptyState()}
                                <br />
                                Identifier: {additionalStatus.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const groupsStrings: string[] = [];
            const groups =
                provider.data.availableGroups ?
                    provider.data.availableGroups.map((group) => {
                        groupsStrings.push(group.name);
                        return (
                            <>
                                Group Name: {group.name || customEmptyState()}
                                <br />
                                Group ID: {group.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const memberStrings: string[] = [];
            const members =
                provider.data.availableMembers ?
                    provider.data.availableMembers.map((member) => {
                        memberStrings.push(member.profile.name);
                        return (
                            <>
                                Member Name: {member.profile.name || customEmptyState()}
                                <br />
                                Member ID: {member.id || customEmptyState()}
                                <br />
                                Member Profile ID: {member.profile.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available groups</Text>}>
                        {makeTooltipAndPreview('available groups', groupsStrings, groups)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available members</Text>}>
                        {makeTooltipAndPreview('available members', memberStrings, members)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Done statuses</Text>}>
                        {makeTooltipAndPreview('done statuses', doneStatusesStrings, doneStatuses)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>All statuses</Text>}>
                        {makeTooltipAndPreview('statuses', additionalStatusesStrings, additionalAvailableDoneStatuses)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Slack: {
            const scopesPreviewStrings = provider.data.scopes ? provider.data.scopes.split(',') : [];
            const scopes =
                provider.data.scopes ?
                    provider.data.scopes.split(',').map((scope) => {
                        return (
                            <>
                                <Text code>{scope}</Text>
                                <br />
                            </>
                        );
                    })
                :   [];

            const channelsPreviewStrings: string[] = [];
            const channels =
                provider.data.availableChannels ?
                    provider.data.availableChannels.map((channel) => {
                        channelsPreviewStrings.push(channel.name);
                        return (
                            <>
                                Channel Name: {channel.name || customEmptyState()}
                                <br />
                                Channel ID: {channel.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const groupsPreviewStrings: string[] = [];
            const groups =
                provider.data.availableGroups ?
                    provider.data.availableGroups.map((group) => {
                        groupsPreviewStrings.push(group.name);
                        return (
                            <>
                                Group Name: {group.name || customEmptyState()}
                                <br />
                                Group ID: {group.id || customEmptyState()}
                                <br />
                                Group Handle: {group.handle || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Slack team</Text>}>
                        {provider.data.slackTeam || customEmptyState('slack team')}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available groups</Text>}>
                        {makeTooltipAndPreview('available groups', groupsPreviewStrings, groups)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available channels</Text>}>
                        {makeTooltipAndPreview('available channels', channelsPreviewStrings, channels)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Scopes</Text>}>
                        {makeCodedTooltipPreview('available scopes', scopesPreviewStrings, scopes)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.TestRail: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project ID: {project.id || customEmptyState()}
                                <br />
                                Is Single Suite:{' '}
                                {project.isSingleSuite === true ?
                                    'True'
                                : project.isSingleSuite === false ?
                                    'False'
                                :   customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            const statusesPreviewStrings: string[] = [];
            const statuses =
                provider.data.allStatuses ?
                    provider.data.allStatuses.map((status) => {
                        statusesPreviewStrings.push(status.displayableName);
                        return (
                            <>
                                Status Name: {status.displayableName || customEmptyState()}
                                <br />
                                Status ID: {status.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];

            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available statuses</Text>}>
                        {makeTooltipAndPreview('available statuses', statusesPreviewStrings, statuses)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Username</Text>}>
                        {provider.data.username || customEmptyState('username')}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Base URL </Text>}>
                        {provider.data.baseUrl || customEmptyState('base URL')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.BrowserStack: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project Identifier: {project.identifier || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Username</Text>}>
                        {provider.data.username || customEmptyState('username')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Qase: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.title);
                        return (
                            <>
                                Project Title: {project.title || customEmptyState()}
                                <br />
                                Project Code: {project.code || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Testmo: {
            const projectsPreviewStrings: string[] = [];
            const projects =
                provider.data.availableProjects ?
                    provider.data.availableProjects.map((project) => {
                        projectsPreviewStrings.push(project.name);
                        return (
                            <>
                                Project Name: {project.name || customEmptyState()}
                                <br />
                                Project ID: {project.id || customEmptyState()}
                                <br />
                                <br />
                            </>
                        );
                    })
                :   [];
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Site</Text>}>
                        {provider.data.baseUrl || customEmptyState('site')}
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Available projects</Text>}>
                        {makeTooltipAndPreview('available projects', projectsPreviewStrings, projects)}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.TravisCI: {
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Host URL</Text>}>
                        {provider.data.hostURL || customEmptyState('host URL')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Xray: {
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Client ID</Text>}>
                        {provider.data.clientId || customEmptyState('client id')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        case IntegrationProviderType.Luciq: {
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                    <Descriptions.Item label={<Text>Dashboard user email</Text>}>
                        {provider.data.email || customEmptyState('email')}
                    </Descriptions.Item>
                </Descriptions>
            );
        }
        default:
            return (
                <Descriptions {...providerDetailsDescProps}>
                    <Descriptions.Item label={<Text>Provider name</Text>}>
                        <ProviderNameCell orgId={orgId} provider={provider} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<Text>Provider ID</Text>}>{provider.id}</Descriptions.Item>
                    <Descriptions.Item label={connectedAppsLabel}>{makeConnectedAppsContent()}</Descriptions.Item>
                    {installationIdsItem}
                </Descriptions>
            );
    }
};
